/**
 * @file Ecran Reglages : langue FR/EN, volumes, mute, eco, qualite, vibrations, chiffres de degats, reduire les animations, auto move/fire/upgrade, export/import/reset, infos GPU (dev).
 */
import type { SettingsData } from '@core/index';
import type { Locale } from '@shared/enums';
import type { TranslationKey } from '@i18n/index';
import { Component } from '../core/Component';
import { h, setText, toggleClass } from '../core/dom';
import { confirmAsk, notify, send } from '../core/feedback';
import { sheetHeader } from '../core/widgets';
import type { UiContext } from '../UiContext';

type BoolKey = 'muted' | 'eco' | 'vibration' | 'damageNumbers' | 'reduceMotion' | 'autoMove' | 'autoFire' | 'autoUpgrade' | 'fxSelf' | 'fxOthers';
type VolumeKey = 'sfxVolume' | 'musicVolume';

const TOGGLES: ReadonlyArray<{ key: BoolKey; label: TranslationKey }> = [
  { key: 'muted', label: 'settings.mute' },
  { key: 'eco', label: 'settings.eco' },
  { key: 'vibration', label: 'settings.vibration' },
  { key: 'damageNumbers', label: 'settings.damageNumbers' },
  { key: 'fxSelf', label: 'settings.fxSelf' },
  { key: 'fxOthers', label: 'settings.fxOthers' },
  { key: 'reduceMotion', label: 'settings.reduceMotion' },
  { key: 'autoMove', label: 'settings.autoMove' },
  { key: 'autoFire', label: 'settings.autoFire' },
  { key: 'autoUpgrade', label: 'settings.autoUpgrade' },
];
const VOLUMES: ReadonlyArray<{ key: VolumeKey; label: TranslationKey }> = [
  { key: 'sfxVolume', label: 'settings.sound' },
  { key: 'musicVolume', label: 'settings.music' },
];
const LOCALES: readonly Locale[] = ['fr', 'en'];
const GPU_PERIOD = 1;

export class SettingsScreen extends Component {
  private readonly toggles = new Map<BoolKey, HTMLElement>();
  private readonly sliders = new Map<VolumeKey, HTMLInputElement>();
  private readonly localeButtons = new Map<Locale, HTMLElement>();
  private readonly qualityButtons = new Map<SettingsData['quality'], HTMLElement>();
  private readonly dragging = new Set<VolumeKey>();
  private exportBox!: HTMLTextAreaElement;
  private gpuBox: HTMLElement | null = null;
  private gpuAcc = GPU_PERIOD;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'screen settings');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.el.setAttribute('data-ui-block', '');
    const body = h('div', { class: 'sheet-body' });

    // ---- langue
    const langRow = h('div', { class: 'seg' });
    for (const l of LOCALES) {
      const b = h('button', { class: 'seg-btn', type: 'button', 'data-ui-block': true, onclick: () => this.setLocale(l) }, l.toUpperCase());
      this.localeButtons.set(l, b);
      langRow.append(b);
    }
    body.append(this.row(t.t('settings.language'), langRow));

    // ---- volumes
    for (const v of VOLUMES) {
      const input = h('input', { class: 'slider', type: 'range', min: 0, max: 1, step: 0.05, 'data-ui-block': true, 'aria-label': t.t(v.label) });
      this.on(input, 'input', () => {
        void send(this.ctx, { type: 'settings.set', patch: v.key === 'sfxVolume' ? { sfxVolume: Number(input.value) } : { musicVolume: Number(input.value) } });
      });
      this.on(input, 'pointerdown', () => this.dragging.add(v.key));
      this.on(input, 'pointerup', () => this.dragging.delete(v.key));
      this.on(input, 'pointercancel', () => this.dragging.delete(v.key));
      this.on(input, 'change', () => this.dragging.delete(v.key));
      this.sliders.set(v.key, input);
      body.append(this.row(t.t(v.label), input));
    }

    // ---- qualite
    const quality = h('div', { class: 'seg' });
    for (const q of ['low', 'high'] as const) {
      const b = h('button', { class: 'seg-btn', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'settings.set', patch: { quality: q } }) }, t.t(q === 'low' ? 'settings.quality.low' : 'settings.quality.high'));
      this.qualityButtons.set(q, b);
      quality.append(b);
    }
    body.append(this.row(t.t('settings.quality'), quality), h('p', { class: 'hint' }, t.t('settings.quality.hint')));

    // ---- interrupteurs
    for (const tg of TOGGLES) {
      const sw = h('button', { class: 'switch', type: 'button', role: 'switch', 'data-ui-block': true, 'aria-label': t.t(tg.label), onclick: () => this.flip(tg.key) }, h('span', { class: 'switch-knob' }));
      this.toggles.set(tg.key, sw);
      body.append(this.row(t.t(tg.label), sw));
    }

    // ---- sauvegarde : export / import / reset
    this.exportBox = h('textarea', { class: 'save-box', readonly: true, rows: 3, 'data-ui-block': true, 'aria-label': t.t('settings.export') });
    const exportBtn = h('button', { class: 'btn', type: 'button', 'data-ui-block': true, onclick: () => this.exportSave() }, t.t('settings.export'));
    const copyBtn = h('button', { class: 'btn', type: 'button', 'data-ui-block': true, onclick: () => void this.copy() }, t.t('settings.copy'));
    const importBox = h('textarea', { class: 'save-box', rows: 3, placeholder: t.t('settings.importHint'), 'data-ui-block': true, 'aria-label': t.t('settings.import') });
    const importBtn = h('button', { class: 'btn', type: 'button', 'data-ui-block': true, onclick: () => void this.importSave(importBox.value) }, t.t('settings.import'));
    const resetBtn = h('button', { class: 'btn danger', type: 'button', 'data-ui-block': true, onclick: () => void this.reset() }, t.t('settings.resetSave'));
    body.append(
      h('div', { class: 'settings-block' }, h('div', { class: 'btn-row' }, exportBtn, copyBtn), this.exportBox),
      h('div', { class: 'settings-block' }, importBox, h('div', { class: 'btn-row' }, importBtn)),
      h('div', { class: 'settings-block' }, resetBtn),
    );

    // ---- debug GPU (mode dev uniquement)
    if (import.meta.env.DEV) {
      this.gpuBox = h('pre', { class: 'gpu-box' });
      body.append(h('div', { class: 'settings-block' }, h('div', { class: 'settings-label' }, t.t('settings.debug')), this.gpuBox));
    }

    this.el.append(sheetHeader(this.ctx, t.t('settings.title')), body);
    this.update(0);
  }

  private row(label: string, control: HTMLElement): HTMLElement {
    return h('div', { class: 'settings-row' }, h('span', { class: 'settings-label' }, label), control);
  }

  private flip(key: BoolKey): void {
    const next = !this.ctx.sim.data.settings[key];
    const patch: Partial<SettingsData> = {};
    patch[key] = next;
    void send(this.ctx, { type: 'settings.set', patch });
    // confirmation tactile quand on REactive les vibrations (le reglage est repris par l'app a la frame suivante)
    if (key === 'vibration' && next) window.setTimeout(() => this.ctx.haptic('medium'), 60);
    this.update(0);
  }

  private setLocale(l: Locale): void {
    if (l === this.ctx.i18n.locale) return;
    void send(this.ctx, { type: 'settings.set', patch: { locale: l } });
    // Declenche la reconstruction complete de l'UI (UiApp ecoute onChange).
    this.ctx.i18n.setLocale(l);
  }

  private exportSave(): void {
    this.exportBox.value = this.ctx.actions.exportSave();
    this.exportBox.select();
  }

  private async copy(): Promise<void> {
    if (!this.exportBox.value) this.exportSave();
    try {
      await navigator.clipboard.writeText(this.exportBox.value);
      notify(this.ctx.i18n.t('settings.copied'), 'success');
    } catch {
      this.exportBox.select();
    }
  }

  private async importSave(json: string): Promise<void> {
    const text = json.trim();
    if (!text) return;
    const ok = await this.ctx.actions.importSave(text);
    if (!ok) notify(this.ctx.i18n.t('settings.importFailed'), 'warning');
  }

  private async reset(): Promise<void> {
    if (await confirmAsk('settings.resetConfirm')) await this.ctx.actions.resetSave();
  }

  override update(dt: number): void {
    const s = this.ctx.sim.data.settings;
    for (const [key, sw] of this.toggles) {
      const on = s[key];
      toggleClass(sw, 'on', on);
      sw.setAttribute('aria-checked', String(on));
    }
    for (const [key, input] of this.sliders) {
      if (this.dragging.has(key)) continue;
      const v = String(s[key]);
      if (input.value !== v) input.value = v;
    }
    for (const [l, b] of this.localeButtons) toggleClass(b, 'active', l === s.locale);
    for (const [q, b] of this.qualityButtons) toggleClass(b, 'active', q === s.quality);
    if (this.gpuBox) {
      this.gpuAcc += dt;
      if (this.gpuAcc >= GPU_PERIOD) {
        this.gpuAcc = 0;
        setText(this.gpuBox, JSON.stringify(this.ctx.actions.gpuInfo(), null, 1));
      }
    }
  }
}
